import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum } from '../types';
import { Button } from '@components/common/Button';
import { FormCurrencyField } from '@components/common/FormCurrencyField/FormCurrencyField';
import { InputAdornment } from '@components/common/InputAdornment';
import { loanRepaymentsQuestions } from './questions';
import { ILoanRepaymentsForm } from './types';
import { getCurrentRoute } from '@utils/location';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';

export const LoanRepayments = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<ILoanRepaymentsForm>({
    defaultValues: {
      securedLoans: '',
      unsecuredLoans: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: ILoanRepaymentsForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: loanRepaymentsQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.CARDS);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <FormattedMessage id={'applicationForm.LOAN_REPAYMENTS.header'} tagName={'h4'} />
        {Object.entries(loanRepaymentsQuestions).map(([key, question]) => (
          <div key={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            <FormattedMessage id={question.explanationKey} tagName={'p'} />
            <FormCurrencyField
              textFieldProps={{
                InputProps: {
                  endAdornment: (
                    <InputAdornment position={'end'}>
                      {intl.formatMessage({ id: question.adornmentKey })}
                    </InputAdornment>
                  ),
                },
              }}
              name={key}
              rules={{ required: requiredFieldErrorMessage, min: 0 }}
            />
          </div>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
